import { motion, useReducedMotion } from 'framer-motion';
import { BellRing, BookOpen, HandHeart, MessagesSquare, ShieldCheck, type LucideIcon } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1] as const;

// Tightened to one punchy line each — depth lives in the FAQ.
const PILLARS: { icon: LucideIcon; title: string; body: string }[] = [
    {
        icon: BookOpen,
        title: 'Free training, from day one',
        body: 'A structured four-part program at no cost — most agents graduate within a month. Already producing over $1M a year? Fast-track and skip ahead.',
    },
    {
        icon: HandHeart,
        title: 'Mentorship & support',
        body: 'A dedicated Sales Manager for your first six months, then direct access to our Luxury Sales Manager, Director of Groups, Director of Concierge Services, and other seasoned industry experts as part of your support infrastructure.',
    },
    {
        icon: MessagesSquare,
        title: 'A community you actually use',
        body: 'Active chats and forums, plus dozens of vendor spotlight calls — real time with hotel sales managers and the key personnel behind the properties you book.',
    },
    {
        icon: BellRing,
        title: 'In-house concierge, Forbes Five-Star trained',
        body: 'Complimentary on bookings over $1,000/night — and you keep your full commission. They handle the logistics so you stay focused on selling.',
    },
];

/** "What you get" infrastructure pillars. Placed after the economics argument. */
export default function WhatYouGet() {
    const shouldReduceMotion = useReducedMotion();

    return (
        <section className="agent-px bg-[#0e2727] py-20 sm:py-28">
            <div className="mx-auto max-w-5xl">
                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-16"
                >
                    <span className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/60">What We Provide</span>
                    <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">Support beyond the tech.</h2>
                </motion.div>

                <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
                    {PILLARS.map((item, i) => {
                        const Icon = item.icon;
                        return (
                            <motion.div
                                key={i}
                                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                                className="flex gap-5"
                            >
                                <Icon className="mt-1 h-5 w-5 shrink-0 text-[#F5F5F5]/40" strokeWidth={1.5} />
                                <div>
                                    <h3 className="lux-body lux-body-sm mb-2 font-medium text-[#F5F5F5]/90">{item.title}</h3>
                                    <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/60">{item.body}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    className="mt-16 flex items-center gap-4"
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
                >
                    <ShieldCheck className="h-4 w-4 shrink-0 text-[#F5F5F5]/40" strokeWidth={1.5} />
                    <p className="lux-body lux-body-sm text-[#F5F5F5]/60">
                        No subscription fees. No membership fees. No client-facing service fees. This is a 1099 independent contractor model.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
